/**
 * 设备绑定页面
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBindings, bindDeviceById, unbindDeviceById } from '../services/device';
import Loading from '../components/Loading';
import Modal from '../components/Modal';
import ErrorAlert from '../components/ErrorAlert';
import EmptyState from '../components/EmptyState';
import './BindDevice.css';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const BindDevice = () => {
  const navigate = useNavigate();
  const [bindings, setBindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [newDeviceId, setNewDeviceId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    loadBindings();
  }, []);

  const loadBindings = async () => {
    setLoading(true);
    try {
      setBindings(await getBindings());
      setError('');
    } catch (err) {
      setError('获取绑定列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    const deviceId = newDeviceId.trim();
    if (!deviceId) {
      setError('请输入设备ID');
      return;
    }
    if (!UUID_REGEX.test(deviceId)) {
      setError('设备ID格式不正确，应为UUID格式');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await bindDeviceById(deviceId);
      setNewDeviceId('');
      setShowModal(false);
      await loadBindings();
    } catch (err) {
      setError(err.message || '绑定失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (deviceId) => {
    if (!window.confirm(`确定要解绑设备 ${deviceId} 吗？`)) return;
    
    setDeletingId(deviceId);
    try {
      await unbindDeviceById(deviceId);
      await loadBindings();
    } catch (err) {
      setError(err.message || '解绑失败');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return <Loading fullscreen text="加载中..." />;
  }

  return (
    <div className="page-container bind-page">
      <header className="page-header">
        <button className="btn-back" onClick={() => navigate('/', { state: { refresh: Date.now() } })}>←</button>
        <div className="page-header-title">
          <h1>设备绑定管理</h1>
        </div>
        <button className="btn-icon" onClick={loadBindings}>🔄</button>
      </header>

      <div className="bind-content">
        <ErrorAlert message={error} onClose={() => setError('')} />

        <div className="bind-actions">
          <span className="bind-count">已绑定 {bindings.length} 个设备</span>
          <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>+ 添加设备</button>
        </div>

        {bindings.length === 0 ? (
          <div className="card">
            <EmptyState 
              icon="📱"
              title="暂无绑定设备"
              action={<button className="btn btn-primary" onClick={() => setShowModal(true)}>添加第一个设备</button>}
            />
          </div>
        ) : (
          <div className="bind-list card">
            {bindings.map((binding) => (
              <div key={binding.id || binding.device_id} className="bind-item">
                <div className="bind-icon">📱</div>
                <div className="bind-info">
                  <div className="bind-id">{binding.device_id}</div>
                  <div className="bind-time">
                    绑定时间: {binding.created_at ? new Date(binding.created_at).toLocaleString() : '-'}
                  </div>
                </div>
                <button
                  className="delete-btn"
                  onClick={() => handleDelete(binding.device_id)}
                  disabled={deletingId === binding.device_id}
                >
                  {deletingId === binding.device_id ? '...' : '🗑️'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal
        visible={showModal}
        title="添加设备"
        onClose={() => !submitting && setShowModal(false)}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={submitting}>取消</button>
            <button className="btn btn-primary" onClick={handleAdd} disabled={submitting || !newDeviceId.trim()}>
              {submitting ? '绑定中...' : '确定绑定'}
            </button>
          </>
        }
      >
        <div className="form-group">
          <label className="form-label">设备ID (UUID)</label>
          <input
            type="text"
            className="form-input"
            placeholder="例如: 61a6dbea-786d-4868-8cc8-5efcc7e38a27"
            value={newDeviceId}
            onChange={(e) => setNewDeviceId(e.target.value)}
            disabled={submitting}
            autoFocus
          />
          <p className="form-hint">请输入设备的UUID，可在设备端查看</p>
        </div>
      </Modal>
    </div>
  );
};

export default BindDevice;
